import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Progress } from '../../components/ui/progress';
import {
  Users, Building2, AlertTriangle, Clock, Calendar, Search, RefreshCcw,
  ChevronRight, ArrowRight, Bell, Check, AlertCircle, Timer, TrendingUp,
  Briefcase, Filter, Eye, FileText, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AlternantsDashboardPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [alternantsList, setAlternantsList] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAlertDetails, setShowAlertDetails] = useState(null);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, listRes] = await Promise.all([
        api.get('/alternants/dashboard'),
        api.get('/alternants/list')
      ]);
      setDashboardData(dashRes.data);
      setAlternantsList(listRes.data.alternants);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Erreur lors du chargement du tableau de bord');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Filter alternants
  const filteredAlternants = alternantsList.filter(alt => {
    // Status filter
    if (selectedFilter !== 'all') {
      if (selectedFilter === 'with_company' && !alt.has_company) return false;
      if (selectedFilter === 'without_company' && alt.has_company) return false;
      if (selectedFilter === 'critical' && alt.contract_status.status !== 'critical') return false;
      if (selectedFilter === 'warning' && alt.contract_status.status !== 'warning') return false;
      if (selectedFilter === 'attention' && alt.contract_status.status !== 'attention') return false;
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = alt.full_name.toLowerCase().includes(query);
      const companyMatch = alt.company?.name?.toLowerCase().includes(query);
      if (!nameMatch && !companyMatch) return false;
    }
    
    return true;
  });

  // Status badge component
  const StatusBadge = ({ status }) => {
    const styles = {
      critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      attention: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
      upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      ended: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
      incomplete: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
    };
    
    const icons = {
      critical: <AlertTriangle className="w-3 h-3" />,
      warning: <AlertCircle className="w-3 h-3" />,
      attention: <Clock className="w-3 h-3" />,
      active: <Check className="w-3 h-3" />,
      upcoming: <Calendar className="w-3 h-3" />,
      ended: <Timer className="w-3 h-3" />,
      incomplete: <AlertCircle className="w-3 h-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status.status] || styles.incomplete}`}>
        {icons[status.status]}
        {status.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Header title="Suivi des Alternants" onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCcw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const alerts = dashboardData?.alerts || {};
  const companies = dashboardData?.companies || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Header title="Suivi des Alternants" onMenuClick={onMenuClick} />
      
      <main className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="col-span-1" data-testid="stats-total">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                  <Users className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                  <p className="text-xs text-slate-500">Alternants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="col-span-1" data-testid="stats-with-company">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.with_company}</p>
                  <p className="text-xs text-slate-500">En entreprise</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="col-span-1" data-testid="stats-critical">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.ending_30_days}</p>
                  <p className="text-xs text-slate-500">&lt; 30 jours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="col-span-1" data-testid="stats-warning">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.ending_60_days}</p>
                  <p className="text-xs text-slate-500">30-60 jours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="col-span-1" data-testid="stats-attention">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <Bell className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.ending_90_days}</p>
                  <p className="text-xs text-slate-500">60-90 jours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="col-span-1" data-testid="stats-active">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.active}</p>
                  <p className="text-xs text-slate-500">Actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts Section */}
        {dashboardData?.total_alerts > 0 && (
          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Alertes Contrats ({dashboardData.total_alerts})
              </CardTitle>
              <CardDescription>Contrats arrivant à échéance prochainement</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Critical Alerts */}
                {alerts.critical?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Urgent (&lt;30 jours)
                    </h4>
                    {alerts.critical.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <p className="font-medium text-sm">{alert.student_name}</p>
                        {alert.company_name && (
                          <p className="text-xs text-slate-500">{alert.company_name}</p>
                        )}
                        <p className="text-xs text-red-600 font-medium mt-1">
                          Fin: {alert.end_date} ({alert.days_remaining} jours)
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Warning Alerts */}
                {alerts.warning?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-amber-600 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Attention (30-60 jours)
                    </h4>
                    {alerts.warning.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                        <p className="font-medium text-sm">{alert.student_name}</p>
                        {alert.company_name && (
                          <p className="text-xs text-slate-500">{alert.company_name}</p>
                        )}
                        <p className="text-xs text-amber-600 font-medium mt-1">
                          Fin: {alert.end_date} ({alert.days_remaining} jours)
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Attention Alerts */}
                {alerts.attention?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-yellow-600 flex items-center gap-1">
                      <Bell className="w-4 h-4" />
                      À surveiller (60-90 jours)
                    </h4>
                    {alerts.attention.map((alert, idx) => (
                      <div key={idx} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <p className="font-medium text-sm">{alert.student_name}</p>
                        {alert.company_name && (
                          <p className="text-xs text-slate-500">{alert.company_name}</p>
                        )}
                        <p className="text-xs text-yellow-600 font-medium mt-1">
                          Fin: {alert.end_date} ({alert.days_remaining} jours)
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Companies Overview */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-600" />
                Par Entreprise
              </CardTitle>
              <CardDescription>{companies.length} entreprise(s) avec alternants</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {companies.length === 0 ? (
                <div className="text-center py-4">
                  <Building2 className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-sm text-slate-500">Aucune entreprise avec alternants</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-2"
                    onClick={() => navigate('/admin/companies')}
                  >
                    Gérer les entreprises
                  </Button>
                </div>
              ) : (
                companies.map((company) => (
                  <div 
                    key={company.id} 
                    className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/companies`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{company.name}</p>
                        {company.city && (
                          <p className="text-xs text-slate-500">{company.city}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 dark:bg-violet-900/30 rounded-full text-xs font-medium text-violet-700 dark:text-violet-400">
                          <Users className="w-3 h-3" />
                          {company.count}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    {/* Mini progress bar showing student statuses */}
                    <div className="mt-2 flex gap-1">
                      {company.students.map((s, idx) => (
                        <div 
                          key={idx}
                          className={`h-1.5 flex-1 rounded-full ${
                            s.status === 'critical' ? 'bg-red-500' :
                            s.status === 'warning' ? 'bg-amber-500' :
                            s.status === 'attention' ? 'bg-yellow-500' :
                            s.status === 'active' ? 'bg-emerald-500' :
                            'bg-slate-300'
                          }`}
                          title={`${s.name}: ${s.status}`}
                        />
                      ))}
                    </div>
                  </div>
                ))
              )}
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/admin/companies')}
              >
                <Building2 className="w-4 h-4 mr-2" />
                Voir toutes les entreprises
              </Button>
            </CardContent>
          </Card>

          {/* Alternants List */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    Liste des Alternants
                  </CardTitle>
                  <CardDescription>{filteredAlternants.length} alternant(s)</CardDescription>
                </div>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Rechercher..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-48"
                      data-testid="search-alternants"
                    />
                  </div>
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
                    data-testid="filter-status"
                  >
                    <option value="all">Tous</option>
                    <option value="with_company">En entreprise</option>
                    <option value="without_company">Sans entreprise</option>
                    <option value="critical">Urgent (&lt;30j)</option>
                    <option value="warning">Attention (30-60j)</option>
                    <option value="attention">À surveiller (60-90j)</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredAlternants.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Aucun alternant trouvé</p>
                  </div>
                ) : (
                  filteredAlternants.map((alt) => (
                    <div 
                      key={alt.id}
                      className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{alt.full_name}</p>
                            <StatusBadge status={alt.contract_status} />
                          </div>
                          
                          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                            {alt.company && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {alt.company.name}
                              </span>
                            )}
                            {alt.training_start_formatted && alt.training_end_formatted && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {alt.training_start_formatted} → {alt.training_end_formatted}
                              </span>
                            )}
                            {alt.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {alt.email}
                              </span>
                            )}
                          </div>
                          
                          {/* Progress bar for contract duration */}
                          {alt.contract_status.days_remaining !== null && alt.contract_status.status !== 'upcoming' && alt.contract_status.status !== 'ended' && (
                            <div className="mt-2">
                              <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Progression contrat</span>
                                <span>{alt.contract_status.days_remaining} jours restants</span>
                              </div>
                              <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    alt.contract_status.status === 'critical' ? 'bg-red-500' :
                                    alt.contract_status.status === 'warning' ? 'bg-amber-500' :
                                    alt.contract_status.status === 'attention' ? 'bg-yellow-500' :
                                    'bg-emerald-500'
                                  }`}
                                  style={{ 
                                    width: `${Math.max(5, 100 - (alt.contract_status.days_remaining / 365) * 100)}%` 
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-1 ml-4">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/students`)}
                            title="Voir le profil"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/documents`)}
                            title="Documents"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/admin/companies')}>
                <Building2 className="w-4 h-4 mr-2" />
                Gérer les entreprises
              </Button>
              <Button variant="outline" onClick={() => navigate('/students')}>
                <Users className="w-4 h-4 mr-2" />
                Gérer les étudiants
              </Button>
              <Button variant="outline" onClick={() => navigate('/documents')}>
                <FileText className="w-4 h-4 mr-2" />
                Générer une convention
              </Button>
              <Button variant="outline" onClick={fetchDashboard}>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AlternantsDashboardPage;
